import { Trans, t } from "@lingui/macro";
import { PlusCircle, Edit2, MinusCircle, Trash2 } from 'lucide-react';
import { useReducer, useState } from "react";
import { ChildBot, Soribot } from "../../models/Soribot";
import { Fire } from "../../services/FireClient";
import { Util } from "../../services/Utils/Util";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { useToast } from "../../hooks/use-toast";
import { Progress } from "../../components/ui/progress";
import { IconPlus } from '../components/FigmaIcons';

export const SoribotDetailsBrokerPart: React.FC<{ bot: Soribot, mode: 'view' | 'edit' }> = ({ bot, mode }) => {
  const [showAddChildBotModal, setShowAddChildBotModal] = useState(false);
  const { toast } = useToast();
  const [_, forceUpdate] = useReducer(x => x + 1, 0);
  const [editScopeDialog, setEditScopeDialog] = useState<{ open: boolean, cb: ChildBot | null, scope: string }>({ open: false, cb: null, scope: "" });

  return <>
    <div className="flex items-center justify-between py-2 border-b mb-2">
      <h4 className="text-sm font-semibold text-muted-foreground uppercase"><Trans>Child Bots</Trans></h4>
      {mode === 'edit' && <Button variant="ghost" size="sm" onClick={() => setShowAddChildBotModal(true)}><PlusCircle className="w-4 h-4" /></Button>}
    </div>

    {bot.childBots && bot.childBots.length === 0 && <div className="p-4 text-center text-muted-foreground text-sm"><Trans>No child bots added</Trans></div>}

    <div className="space-y-2">
      {bot.childBots?.map(cb => (
        <div key={cb.id} className="flex items-center gap-3 p-2 bg-background rounded border">
          <Avatar className="w-10 h-10">
            <AvatarImage src={cb.imageUrl || Util.ANONYMOUS_PHOTO_URL} />
            <AvatarFallback>{cb.name[0]}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm truncate">{cb.name}</div>
            <div className="text-xs text-muted-foreground truncate">{cb.scope || t`No scope defined`}</div>
          </div>
          {mode === 'edit' && <Button variant="ghost" size="icon" onClick={() => editChildBot(cb)}><Edit2 className="w-4 h-4 text-muted-foreground" /></Button>}
        </div>
      ))}
    </div>

    {showAddChildBotModal && <AddRemoveChildBotModal bot={bot} onClose={addChildBotModalClosed} />}

    <Dialog open={editScopeDialog.open} onOpenChange={(open) => setEditScopeDialog(prev => ({ ...prev, open }))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle><Trans>Edit scope</Trans></DialogTitle>
          <DialogDescription><Trans>Define the scope for this child bot.</Trans></DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="scope" className="text-right"><Trans>Scope</Trans></Label>
            <Input id="scope" value={editScopeDialog.scope} onChange={(e) => setEditScopeDialog(prev => ({ ...prev, scope: e.target.value }))} className="col-span-3" />
          </div>
        </div>
        <DialogFooter className="flex justify-between sm:justify-between">
          <Button variant="destructive" onClick={deleteChildBot}><Trans>Delete</Trans></Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditScopeDialog(prev => ({ ...prev, open: false }))}><Trans>Cancel</Trans></Button>
            <Button onClick={saveScope}><Trans>Save</Trans></Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>

  function addChildBotModalClosed() {
    setShowAddChildBotModal(false);
  }

  function editChildBot(cb: ChildBot) {
    setEditScopeDialog({ open: true, cb, scope: cb.scope || "" });
  }

  function saveScope() {
    if (editScopeDialog.cb) {
      editScopeDialog.cb.scope = editScopeDialog.scope;
      forceUpdate();
      setEditScopeDialog(prev => ({ ...prev, open: false }));
    }
  }

  function deleteChildBot() {
    if (editScopeDialog.cb) {
      bot.childBots = bot.childBots.filter(b => b.id !== editScopeDialog.cb!.id);
      bot.childBotIds = bot.childBots.map(b => b.id);
      forceUpdate();
      setEditScopeDialog(prev => ({ ...prev, open: false }));
    }
  }
};

const AddRemoveChildBotModal: React.FC<{ bot: Soribot, onClose: () => void }> = ({ bot, onClose }) => {
  const [bots, setBots] = useState<Soribot[]>();
  const [_, forceUpdate] = useReducer(x => x + 1, 0);

  // Load bots on mount
  useState(() => {
    Fire.bot.getWorkspaceBots(bot.workspaceId).then(list => {
      setBots(list.filter(b => b.subType != 'broker' && b.id != bot.id).sort((a, b) => a.name.localeCompare(b.name)));
    });
  });

  return <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
    <DialogContent className="h-[80vh] flex flex-col p-0">
      <DialogHeader className="p-4 border-b">
        <DialogTitle><Trans>Add or remove bot</Trans></DialogTitle>
        {bots === undefined && <Progress value={undefined} className="h-1" />}
      </DialogHeader>

      <ScrollArea className="flex-1 p-4">
        <div className="space-y-2">
          {bots?.map(b => {
            const isAdded = bot.childBots?.findIndex(cb => cb.id == b.id) >= 0;
            return (
              <div
                key={b.id}
                className={`flex items-center gap-3 p-3 rounded border cursor-pointer hover:bg-muted/50 ${isAdded ? 'bg-muted/50 border-border' : 'bg-background'}`}
                onClick={() => toggleBot(b, isAdded)}
              >
                <Avatar>
                  <AvatarImage src={b.imageUrl || Util.ANONYMOUS_PHOTO_URL} />
                  <AvatarFallback>{b.name[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1 font-bold text-sm">
                  <span className={isAdded ? 'text-muted-foreground' : 'text-foreground'}>{b.name}</span>
                </div>
                {isAdded ? <MinusCircle className="text-muted-foreground w-5 h-5" /> : <IconPlus className="text-blue-500 w-5 h-5" />}
              </div>
            )
          })}
          {bots && bots.length === 0 && <div className="text-center text-muted-foreground py-4"><Trans>No available bots</Trans></div>}
        </div>
      </ScrollArea>
      <DialogFooter className="p-4 border-t">
        <Button onClick={onClose}><Trans>Done</Trans></Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  function toggleBot(b: Soribot, isAdded: boolean) {
    bot.childBots = bot.childBots || [];
    if (isAdded)
      bot.childBots = bot.childBots.filter(cb => cb.id != b.id);
    else
      bot.childBots.push(Soribot.toChildbot(b));
    bot.childBotIds = bot.childBots.map(cb => cb.id);
    forceUpdate();
  }
};